// The Boot screens' live push (spec/09 §6.3, spec/25 §14). While the organisation's realtime socket is live, this
// subscribes to `venue:{v}:boot` (a member holding `boot:view`) and keeps the same cache the screens read:
//
//   boot.server.changed   the boot server's row (status, version, disks, licence) replaces the one in the list
//   boot.pc.changed       a boot PC's row replaces its own (a PC that booted, was assigned or removed)
//                         — and, with boot.server.changed, the Floor's boot state is read again; the New PCs tray too
//   boot.job.progress     a capture, preparation, import, merge, shader wipe or server edit moved on: its newest state
//                         is kept per version for the wizard, and a finished one (or a game disk the boot server just
//                         mounted for updates) re-reads the images and their versions
//
// A frame carries the row it changes, so nothing is fetched for the first two. On every (re)connect the lists are read
// again, so a frame missed while disconnected converges. When the socket is not live the lists poll (`queries.ts`).

import type { BootJobProgressPayload, BootPcChangedPayload, BootServerChangedPayload } from '@clavian/contracts/realtime';
import { channels } from '@clavian/contracts/constants';
import type { TypeId } from '@clavian/contracts/typeid';
import type { BootPc, BootServer } from '@clavian/api-client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { useChannelEvents, useRealtimeLive } from '../realtime';
import { applyPcChanged, applyServerChanged, type JobProgress } from './format';
import { bootKeys, type BootScope } from './queries';

// Named here because `@clavian/contracts/constants` does not carry the boot names; `__tests__/boot-format.test.ts`
// holds them to the contract's own.
export const BOOT_SERVER_CHANGED = 'boot.server.changed';
export const BOOT_PC_CHANGED = 'boot.pc.changed';
export const BOOT_JOB_PROGRESS = 'boot.job.progress';

/** The latest progress per image version, as `boot.job.progress` frames have reported it. */
export type BootJobs = Readonly<Record<string, JobProgress>>;

/** What a progress frame keeps: the job's newest state, filed under the version it is for. */
export function progressFrom(payload: BootJobProgressPayload): { readonly versionId: string; readonly progress: JobProgress } | undefined {
  if (payload.versionId === null) return undefined;
  return {
    versionId: payload.versionId,
    progress: {
      job: payload.job,
      imageId: payload.imageId,
      state: payload.state,
      percent: payload.percent,
      message: payload.message,
      sizeBytes: payload.sizeBytes,
      bytesDone: payload.bytesDone ?? null,
      bytesTotal: payload.bytesTotal ?? null,
      preparationReport: payload.preparationReport,
      at: payload.at,
    },
  };
}

/** Keep the Boot lists live. Returns whether the push is active, so the lists can drop their polling. */
export function useBootLive({ organizationId, venueId }: BootScope, enabled: boolean): boolean {
  const queryClient = useQueryClient();
  const live = useRealtimeLive() && enabled;

  useEffect(() => {
    if (!live) return;
    void queryClient.invalidateQueries({ queryKey: bootKeys.all(organizationId, venueId) });
  }, [live, organizationId, venueId, queryClient]);

  useChannelEvents(
    channels.venue(venueId as TypeId<'ven'>, 'boot'),
    (envelope) => {
      if (envelope.type === BOOT_SERVER_CHANGED) {
        const server = (envelope.p as BootServerChangedPayload | undefined)?.server as BootServer | undefined;
        if (server !== undefined) queryClient.setQueryData<BootServer[]>(bootKeys.servers(organizationId, venueId), (list) => applyServerChanged(list, server));
        // The Floor's boot state follows the server (offline, licence): read it again.
        void queryClient.invalidateQueries({ queryKey: bootKeys.floor(organizationId, venueId) });
        return;
      }
      if (envelope.type === BOOT_PC_CHANGED) {
        const pc = (envelope.p as BootPcChangedPayload | undefined)?.pc as BootPc | undefined;
        if (pc !== undefined) queryClient.setQueryData<BootPc[]>(bootKeys.pcs(organizationId, venueId), (list) => applyPcChanged(list, pc));
        void queryClient.invalidateQueries({ queryKey: bootKeys.floor(organizationId, venueId) });
        // A PC that booted for the first time, or was made a station, enters or leaves the Floor's New PCs tray.
        void queryClient.invalidateQueries({ queryKey: bootKeys.newPcs(organizationId, venueId) });
        return;
      }
      if (envelope.type === BOOT_JOB_PROGRESS) {
        const filed = progressFrom(envelope.p as BootJobProgressPayload);
        if (filed === undefined) return;
        queryClient.setQueryData<BootJobs>(bootKeys.jobs(organizationId, venueId), (jobs) => ({ ...jobs, [filed.versionId]: filed.progress }));
        // A finished job changed the image's versions (a new one, its size, its report): read them again. A server edit
        // the boot server mounted changes the image's open edit.
        if (filed.progress.state !== 'running' || filed.progress.job === 'server_edit') {
          void queryClient.invalidateQueries({ queryKey: bootKeys.images(organizationId, venueId) });
          void queryClient.invalidateQueries({ queryKey: ['boot', organizationId, venueId, 'versions'] });
        }
      }
    },
    live,
  );

  return live;
}

/** The newest progress frames seen, by version. Empty until a job reports; written by `useBootLive`, never fetched. */
export function useBootJobs({ organizationId, venueId }: BootScope): BootJobs {
  const query = useQuery<BootJobs>({ queryKey: bootKeys.jobs(organizationId, venueId), queryFn: () => ({}), enabled: false, initialData: {} });
  return query.data;
}
